<script setup lang="ts">
import type { ReadingPosition } from '~/composables/book/useBookUrl'
import type { InclusionOption } from '~/types/download'
import type { TocItemData } from '~/utils/literature/hierarchy-builder'
import type { Page } from '~/utils/solr/literature'
import { SolrParamsKey } from '#shared/solr'
import { datadogRum } from '@datadog/browser-rum'
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons'
import { consola } from 'consola'
import { useBookTocFallbackNavigation } from '~/composables/book/useBookTocFallbackNavigation'
import { useBookUrl } from '~/composables/book/useBookUrl'
import { usePageLoader } from '~/composables/book/usePageLoader'
import { trackableLegalAreas } from '~/utils/pendo/legal-areas'
import { useTocStateProvider } from '~/composables/book/useTocState'
import { useDownloadAccess } from '~/composables/download/useDownloadAccess'
import { mapBookTocToDownloadNodes } from '~/utils/download/mapBookTocToNodes'
import { redirectLookup } from '~/utils/redirect-lookup';
import { RedirectEntryType } from '#shared/redirect-service-types';

const route = useRoute<'book-by-id'>()
const bookId = computed(() => route.params.bokId?.[0] ?? '')
const searchMode = ref(!!route.query.q)
const selectedSearchResult = ref<{ id: string, highlights: string[] } | null>(null)
// const favorites = useFavorites();

const { activeView, open, back, reset } = useReadingView('book-view-stack')
const { sidebarExpanded } = useReadingSidebarDefault()

reset()
onScopeDispose(() => {
  reset()
})

watch(() => route.query.q, (newValue) => {
  searchMode.value = !!newValue
})

const { canDownload } = useDownloadAccess()

const { loggedIn } = useUserSession()
const hasLicense = useHasContentLicense()
const { isDark: readingDarkMode, toggle: toggleReadingDarkMode } = useReadingMode()

const disableDownloadLabel = computed(() => {
  if (!canDownload.value) {
    return 'Nedlasting ikke tilgjengelig for dette innholdet'
  }
  return undefined
})

const { data: bookData, suspense: bookSuspense } = useBookAndArticleQuery(
  computed(() => ({ [SolrParamsKey.Id]: toValue(bookId), [SolrParamsKey.ContentType]: ['book'] })),
)

const {
  tocItems,
  flatTocItems,
  selectedTocId,
  setSelectedTocId,
  suspense: tocSuspense,
} = useTocStateProvider(bookId)

if (loggedIn.value) {
  await bookSuspense()
}
await tocSuspense()

const book = computed(() => bookData.value?.response?.docs[0] ?? null)

if (loggedIn.value && !book.value) {
  const redirect = await redirectLookup(bookId.value, RedirectEntryType.ISBN);
  if (redirect) {
    /*
    if (favorites.isFavorite("literature", bookId.value)) {
      // Switch favorites with the new redirected book
      await Promise.all([
        favorites.toggleFavorite("literature", bookId.value),
        favorites.toggleFavorite("literature", redirect.target)
      ]);
    }*/
    navigateTo({
      name: 'book-by-id',
      params: {
        bokId: [redirect.target],
      }
    })
  } else {
    throw createError({
    statusCode: 404,
    statusMessage: 'Bok ikke funnet',
    fatal: true,
  })
  }
}

const { urlPosition, updateUrl, updateUrlSilently } = useBookUrl()

const pageLoader = usePageLoader({
  bookId,
  position: urlPosition,
  batchSize: 20,
  enabled: hasLicense,
})

// updateUrlSilently bypasses reactivity, so the floating mobile page indicator needs its own ref
const visiblePageNumber = ref<string | undefined>(urlPosition.value.pageNumber)

// Mobile hides page numbers, so announce the current page for screen readers. Debounced to avoid updates on every scroll frame
const announcedPageNumber = ref<string | undefined>()
watchDebounced(visiblePageNumber, (pageNumber) => {
  if (isMobileViewport()) {
    announcedPageNumber.value = pageNumber
  }
}, { debounce: 1000 })

function syncStateFromScroll(pos: ReadingPosition, tocEntry: TocItemData | null) {
  visiblePageNumber.value = pos.pageNumber ?? tocEntry?.pageNumberString ?? visiblePageNumber.value
  if (tocEntry) {
    setSelectedTocId(tocEntry.id)
    updateUrlSilently({ pageNumber: tocEntry.pageNumberString, anchor: tocEntry.fragmentId })
  }
  else {
    updateUrlSilently(pos)
  }
}

const { handleTocItemSelected: handleFallbackTocItemSelected } = useBookTocFallbackNavigation({
  flatTocItems,
  pageLoader,
  updateUrl,
  onUnresolvedPageNumber: (node) => {
    const logger = import.meta.dev ? consola.error : datadogRum.addError
    logger('TocItem selected without pageNumberString and no next chapter fallback', {
      bookId: toValue(bookId),
      node,
    })
  },
})

async function handleTocItemSelected(node: TocItemData) {
  await handleFallbackTocItemSelected(node)
  back()
}

function handleShowSearchResult(doc: Page, highlights: string[], userInitiated: boolean) {
  if (!doc.pageNumberString) {
    return
  }

  selectedSearchResult.value = { id: doc.id, highlights }
  updateUrl({ pageNumber: doc.pageNumberString, anchor: undefined })
  // A single search hit may auto-show while the user is still typing. Stay in search view unless the user taps a result
  if (userInitiated) {
    back()
  }
}

const bookTocForDownload = computed(() => mapBookTocToDownloadNodes({
  tocItems: tocItems.value,
  book: book.value,
}))

const downloadModal = useTemplateRef('downloadModal')
const contentDiv = useTemplateRef<ComponentPublicInstance | null>('contentDiv')
const { contentTop, contentBottom, headerHeight, updateMeasurements, bannerHeight } = useContentMeasurements(contentDiv)

onMounted(() => {
  updateMeasurements()
  if (isMobileViewport() && route.query.q) {
    open('toc')
  }
})

function openDownloadModal() {
  if (!bookTocForDownload.value.length) {
    return
  }

  const fallbackNode = bookTocForDownload.value[0]
  const activeId = selectedTocId.value || fallbackNode?.id
  if (!activeId) {
    return
  }

  downloadModal.value?.show?.({
    id: activeId,
  })
}

const title = computed(() => book.value?.title ?? 'Faglitteratur')

const breadcrumbOverrides = computed(() => {
  const segments = route.path.split('/')
  return segments.map((_, i) => {
    if (i < 2) {
      return undefined
    }
    if (i === 2) {
      return false
    }
    if (i === 3) {
      return { label: title.value, to: `/faglitteratur/bok/${toValue(bookId)}` }
    }
    return false
  })
})

useTrackLastActivity(title)

usePendoContentTracking({
  type: 'book',
  title,
  id: bookId,
  legalAreas: computed(() => trackableLegalAreas(!!book.value, book.value?.legal_areas)),
})

definePageMeta({
  name: 'book-by-id',
  key: route => `book-${(route.params as { bokId?: string[] }).bokId?.[0] ?? ''}`,
  layout: 'default-with-mobile-notice',
  theme: 'carbon',
  mobileReady: true,
})

useTocExpandProvider(tocItems, (item: TocItemData) => item.children ?? [], (item: TocItemData) => item.id)

useHead({
  bodyAttrs: { class: 'bg-surface-primary reading-dark-scope' },
  title,
})

const inclusionOptions: InclusionOption[] = [
  { key: 'frontPage', label: 'Forside' },
  { key: 'colophonPage', label: 'Kolofonside' },
  { key: 'tableOfContents', label: 'Innholdsfortegnelse' },
]
</script>

<template>
  <div>
    <ContentDiv ref="contentDiv" class="flex items-center">
      <div class="md:hidden flex items-center gap-2xs">
        <ControlButton
          size="small"
          :icon="faArrowLeft"
          icon-size="md"
          aria-label="Tilbake"
          class="shrink-0 !size-11 bg-transparent hover:bg-surface-secondary dark:hover:bg-surface-secondary border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blurple-300"
          @click="navigateTo({ name: 'literature-index' })"
        />
      </div>

      <Breadcrumbs :overrides="breadcrumbOverrides" hide-mobile-back />
      <ReadingActions
        v-if="hasLicense"
        :content="book"
        :disable-download-label="disableDownloadLabel"
        enable-dark-mode
        :dark-mode="readingDarkMode"
        @open-download="openDownloadModal"
        @update:dark-mode="toggleReadingDarkMode"
      />
    </ContentDiv>

    <ReadingLayout
      v-model:sidebar-expanded="sidebarExpanded"
      fixed-content
      :content-top="contentTop"
      :content-bottom="contentBottom"
      :header-height="headerHeight"
      :banner-height="bannerHeight"
      :licensed-content="hasLicense"
      :active-view="activeView"
      context="literature"
    >
      <template #sidebar>
        <LiteratureMobileTableOfContentBar v-model:search-mode="searchMode" mode="close" />
        <TableOfContentToggle
          v-model:search-mode="searchMode"
          title="Innhold"
          show-search-option
          show-content-option
          show-expand-controls
          class="hidden md:flex sticky top-0 z-[9] bg-surface-elevated pb-xs"
        />
        <div class="flex-1 flex flex-col gap-s animate-fade">
          <LiteratureSearch
            v-if="searchMode && hasLicense"
            :book="book"
            :search-mode="searchMode"
            @show-search-result="handleShowSearchResult"
          />
          <ul v-else>
            <TableOfContentItem
              v-for="(child, i) in tocItems"
              :key="child.id"
              :toc-item="child"
              :fallback-index="i + 1"
              :selected-id="selectedTocId"
              :disabled="!hasLicense || pageLoader.isFetching.value"
              @select="handleTocItemSelected"
            />
          </ul>
        </div>
      </template>

      <template #sidebar-collapsed>
        <span class="text-small text-carbon-500 dark:text-carbon-50 select-none font-bold">Innholdsfortegnelse og søk</span>
      </template>

      <template #content="{ isScrollTop }">
        <LiteratureMobileTableOfContentBar v-model:search-mode="searchMode" mode="open" />
        <ReadingNotAuthorized v-if="!hasLicense" />
        <LiteratureContent
          v-else-if="book"
          :key="book.id"
          :search-result="selectedSearchResult"
          :book="book"
          :pages="pageLoader.pages.value"
          :is-loading="pageLoader.isLoading.value"
          :is-fetching="pageLoader.isFetching.value"
          :can-load-more="pageLoader.canLoadMore.value"
          :is-loading-more="pageLoader.isLoadingMore.value"
          :position="urlPosition"
          @load-more="pageLoader.loadMore"
          @visible-position-changed="syncStateFromScroll"
        >
          <template #header="{ isNavigating: contentIsNavigating }">
            <ReadingHeader :content="book" :is-scroll-top="isScrollTop && !contentIsNavigating" :class="isScrollTop && !contentIsNavigating ? 'md:pt-xs' : 'pt-xs'" />
          </template>
        </LiteratureContent>
        <div
          v-if="hasLicense && book && visiblePageNumber"
          aria-hidden="true"
          class="md:hidden fixed bottom-[max(1.5rem,env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 z-20 rounded-full bg-surface-primary border border-border-secondary shadow-md px-s py-2xs text-caption text-secondary select-none pointer-events-none"
        >
          s. {{ visiblePageNumber }}
        </div>
        <div class="sr-only" role="status" aria-live="polite">
          {{ announcedPageNumber ? `Side ${announcedPageNumber}` : '' }}
        </div>
      </template>
    </ReadingLayout>

    <DownloadModal
      v-if="hasLicense"
      ref="downloadModal"
      :toc="bookTocForDownload"
      :selected-id="selectedTocId"
      :inclusion-options="inclusionOptions"
      :show-inclusion-options="true"
      :page-number-selection="true"
      render-leaf-nodes
      no-availability-label="Ingen nedlasting tilgjengelig"
    />
  </div>
</template>
